import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Form, Skeleton, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { FormInstance } from 'antd/lib/form';
import useSwr from '@hooks/useSwr';
import PermissionsCard from '@components/PermissionsCard';
import SessionContext from '@providers/Session/SessionContext';
import { permissionsThatBusinessCannotPassOn } from '@utils/permissionsThatBusinessCannotPassOn';
import rolesConstants from '@utils/rolesConstants';

const { Paragraph } = Typography;
const { Item } = Form;

interface ICustomerPermissionsFormProps {
  form: FormInstance;
  onFormFinish: (values: any) => void;
  parentIdSelected: number;
  customerTypeWillCreated: string;
}

const CustomerPermissionsForm: React.FC<ICustomerPermissionsFormProps> = ({
  form,
  onFormFinish,
  parentIdSelected,
  customerTypeWillCreated = 'CUSTOMER',
}) => {
  const { t } = useTranslation(['customers', 'permissions']);
  const {
    isMotBusiness,
    isRoot,
    customer: { customerLogged },
  } = useContext(SessionContext);
  const defaultParentId = customerLogged?.id;

  const [loading, setLoading] = useState(false);

  const { data } = useSwr(
    `/customers/${parentIdSelected ?? defaultParentId}/roles/`,
    {
      params: {
        customerType: customerTypeWillCreated,
      },
    },
  );

  const dataFiltered = useMemo(() => {
    if (isMotBusiness) {
      return data?.filter(
        (role: any) =>
          !permissionsThatBusinessCannotPassOn.includes(role.name) && role,
      );
    }
    return data;
  }, [data, isMotBusiness]);

  const groupedData = useMemo(
    () =>
      dataFiltered &&
      Object.entries(
        dataFiltered.reduce(
          (groups: any, item: any) => ({
            ...groups,
            [item.category]: [...(groups[item.category] || []), item],
          }),
          {},
        ),
      ),
    [dataFiltered],
  );

  useEffect(() => {
    setLoading(true);
    if (dataFiltered) setLoading(false);
  }, [dataFiltered]);

  const groupedRoles = useMemo(
    () =>
      groupedData?.map(([groupedCategory, permissions]: any) => [
        groupedCategory,
        permissions,
      ]),
    [groupedData],
  );

  return (
    <Skeleton loading={loading} active>
      <Form colon={false} form={form} layout="vertical">
        <Paragraph style={{ padding: '10px 0' }}>
          {t('form.help.permissions')}
        </Paragraph>

        <Item name="roles">
          <PermissionsCard
            groupedRoles={groupedRoles}
            selectedRoles={form.getFieldValue('roles')}
            setSelectedRoles={onFormFinish}
            filterRoles={[
              {
                category: 'support',
                roles: [
                  {
                    name: rolesConstants.VIEW_SERVICE_REQUESTS,
                    visible: isRoot,
                  },
                  {
                    name: rolesConstants.MANAGE_SERVICE_REQUESTS,
                    visible: isRoot,
                  },
                ],
              },
            ]}
          />
        </Item>
      </Form>
    </Skeleton>
  );
};

export default CustomerPermissionsForm;
